import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, ShieldAlert } from "lucide-react";
import { PageHeader } from "@/business/components/PageHeader";
import { clearAuth, MOCK_BUSINESS_API } from "@/business/api";

const NOTIF_KEY = "buskerbeat_business_notif_prefs";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || "") || defaultPrefs(); } catch { return defaultPrefs(); }
}
function defaultPrefs() { return { new_messages: true, new_applications: true, gig_reminders: true, weekly_summary: false, product_updates: true }; }

export default function SettingsPage() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const updPwd = (k) => (e) => setPwdForm((s) => ({ ...s, [k]: e.target.value }));
  const setPref = (k, v) => setPrefs((s) => ({ ...s, [k]: v }));

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pwdForm.next !== pwdForm.confirm) { toast.error("New passwords don’t match."); return; }
    setSavingPwd(true);
    await new Promise((r) => setTimeout(r, 400));
    setSavingPwd(false);
    setPwdForm({ current: "", next: "", confirm: "" });
    if (MOCK_BUSINESS_API) toast.success("Password updated (preview — not persisted to server yet).");
    else toast.success("Password updated.");
  };

  const onSavePrefs = async () => {
    setSavingPrefs(true);
    try { localStorage.setItem(NOTIF_KEY, JSON.stringify(prefs)); } catch {}
    await new Promise((r) => setTimeout(r, 300));
    setSavingPrefs(false);
    toast.success("Notification preferences saved.");
  };

  const onDelete = async () => {
    setDeleting(true);
    await new Promise((r) => setTimeout(r, 400));
    clearAuth();
    setDeleting(false);
    toast.success("Account deleted.");
    navigate("/business/login", { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader title="Settings" description="Manage your account, notifications, and security." />

      {/* Account info */}
      <Card className="border-[#E5E7EB] shadow-sm">
        <CardHeader><h2 className="text-base font-semibold">Account</h2></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Business name</Label><Input value={user?.business_name || ""} readOnly className="bg-[#F9FAFB]" data-testid="settings-business-name-readonly" /></div>
          <div><Label>Email</Label><Input value={user?.email || ""} readOnly className="bg-[#F9FAFB]" data-testid="settings-email-readonly" /></div>
          <p className="md:col-span-2 text-xs text-[#6B7280]">To change these, head over to <a href="/business/dashboard/venue" className="text-[#FF6A00] hover:underline">My Venue</a>.</p>
        </CardContent>
      </Card>

      {/* Change password */}
      <Card className="border-[#E5E7EB] shadow-sm" data-testid="settings-change-password-card">
        <CardHeader><h2 className="text-base font-semibold">Change password</h2></CardHeader>
        <CardContent>
          <form onSubmit={onChangePassword} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="settings-change-password-form">
            <div><Label htmlFor="current">Current password</Label><Input id="current" type="password" value={pwdForm.current} onChange={updPwd("current")} autoComplete="current-password" data-testid="settings-current-password-input" required /></div>
            <div><Label htmlFor="next">New password</Label><Input id="next" type="password" value={pwdForm.next} onChange={updPwd("next")} autoComplete="new-password" data-testid="settings-new-password-input" required minLength={6} /></div>
            <div><Label htmlFor="confirm">Confirm new password</Label><Input id="confirm" type="password" value={pwdForm.confirm} onChange={updPwd("confirm")} autoComplete="new-password" data-testid="settings-confirm-password-input" required minLength={6} /></div>
            <div className="md:col-span-3 flex justify-end">
              <Button type="submit" disabled={savingPwd} className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="settings-change-password-button">
                {savingPwd && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Email notifications */}
      <Card className="border-[#E5E7EB] shadow-sm" data-testid="settings-email-notifications-card">
        <CardHeader><h2 className="text-base font-semibold">Email notifications</h2></CardHeader>
        <CardContent className="space-y-3">
          {[
            { k: "new_messages", label: "New messages", help: "When a busker or customer messages you." },
            { k: "new_applications", label: "New gig applications", help: "When a busker applies to one of your posts." },
            { k: "gig_reminders", label: "Gig reminders", help: "Day-before reminders for upcoming gigs." },
            { k: "weekly_summary", label: "Weekly summary", help: "A digest of your venue’s activity each Monday." },
            { k: "product_updates", label: "Product updates", help: "Occasional updates about new features." },
          ].map((row) => (
            <label key={row.k} className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] p-3 cursor-pointer hover:bg-[#F9FAFB] transition-colors duration-150" data-testid={`settings-pref-row-${row.k}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{row.label}</div>
                <div className="text-xs text-[#6B7280]">{row.help}</div>
              </div>
              <Switch checked={!!prefs[row.k]} onCheckedChange={(v) => setPref(row.k, v)} data-testid={`settings-email-notifications-switch-${row.k}`} />
            </label>
          ))}
          <div className="flex justify-end pt-2"><Button onClick={onSavePrefs} disabled={savingPrefs} className="bg-[#FF6A00] hover:bg-[#E85F00] text-white" data-testid="settings-save-prefs-button">{savingPrefs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save preferences</Button></div>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-red-200 bg-red-50/40 shadow-sm" data-testid="settings-danger-zone-card">
        <CardHeader><h2 className="text-base font-semibold text-[#B91C1C] flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Danger zone</h2></CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Delete account</div>
            <div className="text-xs text-[#6B7280]">This permanently removes your venue profile, gigs, and messages. There is no undo.</div>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="settings-delete-account-button"><ShieldAlert className="mr-2 h-4 w-4" />Delete account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this venue account?</AlertDialogTitle>
                <AlertDialogDescription>This will permanently remove your venue profile, any gigs you’ve posted, and all messages. This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="settings-delete-account-cancel-button">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete} disabled={deleting} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="settings-delete-account-confirm-button">
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Yes, delete my account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
